import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SingleQuery } from '../SingleQuery';

import { useQuery } from './useQuery';

/**
 * Use query url data as state.
 */
export const useQueryHistory = (queryName: string) => {
  const [search, setSearch] = useSearchParams();
  const query = useQuery();

  const singleQuery = useMemo(
    () => new SingleQuery(query, queryName),
    [query, queryName],
  );

  /**
   * History functions
   */
  const replaceHistory = useCallback(
    () => setSearch(query.toString()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, query],
  );

  return {
    value: singleQuery.getValue(),
    values: useMemo(() => singleQuery.getValues(), [singleQuery]),
    includes: singleQuery.includes,
    /**
     * Append a value if it's not present.
     */
    append: useCallback(
      (value: string) => {
        if (singleQuery.includes(value)) {
          return;
        }
        singleQuery.append(value);
        replaceHistory();
      },
      [singleQuery, replaceHistory],
    ),
    /**
     * Removes a value if it's present
     */
    pluck: useCallback(
      (value: string) => {
        const newValues = singleQuery.without(value);
        singleQuery.deleteQuery();

        // If there are no more values left, replace the history without the query.
        if (newValues.length < 0) {
          replaceHistory();
          return;
        }

        // Otherwise we will add back in the values.
        newValues.forEach((val) => singleQuery.append(val));
        replaceHistory();
      },
      [singleQuery, replaceHistory],
    ),
    set: useCallback(
      (value: string) => {
        singleQuery.set(value);
        replaceHistory();
      },
      [singleQuery, replaceHistory],
    ),
    delete: useCallback(() => {
      singleQuery.deleteQuery();
      replaceHistory();
    }, [singleQuery, replaceHistory]),
  };
};
